/**
 * The procedural dubplate label — `design-direction.md` §6.5.
 *
 * Most tracks in the channel have no cover art, so every track gets a *cut* label
 * derived from its own metadata. The whole value of the system is that the derivation
 * is **deterministic forever**: the same `performer|title` must produce the same plate
 * in the indexer, in the API and in the browser, today and after a re-post, a restart
 * or a redeploy.
 *
 * Therefore, inviolable rules for this module:
 *   - no `Math.random`, no `Date.now`, no `crypto`, no ambient state;
 *   - no iteration over an object/Map whose order could change;
 *   - no dependency on `@bl/shared`'s own text pipeline — `normalizeText` (T04) is a
 *     *search* normaliser and is allowed to evolve as search improves. The plate key
 *     normaliser below is **pinned** and must never change, so it lives here as a
 *     private function even though it looks similar;
 *   - the hash is FNV-1a/32 over UTF-16 code units, exactly as spelled in the design
 *     contract, and its output is pinned by literal expected values in the tests.
 *
 * This module imports nothing. It must stay pure so both `apps/web` (Bundler
 * resolution) and the Node apps (NodeNext) can use it unchanged.
 */
/**
 * The six hand-tested ink pairs, frozen and index-stable.
 *
 * **Never generate hues.** The design contract is explicit: procedural hue maths would
 * hand roughly one track in ten an ugly plate and destroy the trust the entire concept
 * rests on. Adding, removing or reordering an entry re-cuts every existing plate, so
 * the array is a permanent part of the data contract — `tracks.plate_pair` stores the
 * index.
 */
export const INK_PAIRS = Object.freeze([
    Object.freeze({ name: 'TROJAN', ink1: '#F04E23', ink2: '#14110D', stock: '#E7DCC1' }),
    Object.freeze({ name: 'REGISTRATION', ink1: '#2B4FB8', ink2: '#F04E23', stock: '#E7DCC1' }),
    Object.freeze({ name: 'OXIDE', ink1: '#C6301C', ink2: '#14110D', stock: '#E7DCC1' }),
    Object.freeze({
        name: 'ONE COLOUR (the cheap cut)',
        ink1: '#14110D',
        ink2: '#837A6A',
        stock: '#E7DCC1',
    }),
    Object.freeze({ name: 'TREASURE ISLE', ink1: '#2B4FB8', ink2: '#14110D', stock: '#E7DCC1' }),
    Object.freeze({ name: 'HALF SPEED', ink1: '#8C4A2F', ink2: '#F04E23', stock: '#CFC3A4' }),
]);
/** How many curated pairs exist. The plate hash is reduced modulo this. */
export const INK_PAIR_COUNT = 6;
/**
 * Resolve a pair index to its frozen {@link InkPair}.
 *
 * Throws `RangeError` on anything that is not an integer in `[0, 5]` — a bad index is a
 * programming error (or a corrupt `plate_pair` column), and silently substituting pair
 * `0` would print the wrong plate rather than surface the fault.
 */
export function inkPairFor(pair) {
    const found = Number.isInteger(pair) ? INK_PAIRS[pair] : undefined;
    if (found === undefined) {
        throw new RangeError(`inkPairFor: pair must be an integer in [0,5], received ${String(pair)}`);
    }
    return found;
}
/* ─────────────────────────────── the hash ──────────────────────────────────── */
/**
 * The pinned plate-key normaliser: casefold, NFKD, then drop every character that is
 * neither a Unicode letter nor a Unicode number.
 *
 * NFKD before the strip is what makes `Sizzla` and `Sïzzla` land on the same plate: the
 * decomposition separates the combining mark, and the strip removes it. It is
 * deliberately more aggressive than search normalisation — spacing, punctuation, `feat.`
 * markers and emoji all vanish — because a plate should survive cosmetic edits to a
 * caption.
 */
function normalizeForPlate(s) {
    return s
        .toLowerCase()
        .normalize('NFKD')
        .replace(/[^\p{L}\p{N}]+/gu, '');
}
/**
 * The seed input: `${norm(performer)}|${norm(title)}`.
 *
 * A missing performer normalises to the empty string rather than a placeholder, so a
 * track that later gains a performer legitimately re-cuts — but a track whose performer
 * is `null` today and `null` tomorrow never does.
 */
export function plateKey(performer, title) {
    return `${normalizeForPlate(performer ?? '')}|${normalizeForPlate(title)}`;
}
/**
 * FNV-1a, 32-bit, over UTF-16 code units.
 *
 * `Math.imul` for the 32×32 multiply and `>>> 0` after every step keep this exactly
 * inside uint32 in every JS engine — a plain `*` would lose the low bits to float64
 * rounding as soon as the product exceeds 2^53 and would produce different plates in
 * different engines. Iterating code units (not code points) is part of the pinned
 * contract; do not "improve" it to `for … of`.
 *
 * @returns an unsigned 32-bit integer.
 */
export function fnv1a(s) {
    let h = 0x811c9dc5;
    for (let i = 0; i < s.length; i++) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 0x01000193) >>> 0;
    }
    return h >>> 0;
}
/* ───────────────────────────── the derivation ──────────────────────────────── */
/**
 * Four digits derived from the Telegram message id: `message_id % 10000`.
 *
 * The design contract writes `messageId % 10000` and assumes a positive integer id,
 * which is what Telegram issues. This wrapper keeps the function *total* for anything
 * else — a truncated, non-negative modulus — because `catalogueNo` is rendered on a
 * label and `BL-00-7` must never reach a screen. For every real (positive integer)
 * message id the result is byte-identical to the contract expression.
 */
function catalogueNumber(messageId) {
    const n = Number.isFinite(messageId) ? Math.trunc(messageId) : 0;
    const wrapped = ((n % 10000) + 10000) % 10000;
    return `BL-${String(wrapped).padStart(4, '0')}`;
}
/**
 * Derive the complete plate for one track. Pure, total and deterministic.
 *
 * Bit budget, straight from `design-direction.md` §6.5 — each field reads a different
 * slice of the hash so `pair`, `pattern`, `tilt` and `side` vary independently:
 *
 * | field     | expression              |
 * |-----------|-------------------------|
 * | `pair`    | `h % 6`                 |
 * | `pattern` | `(h >>> 3) % 3`         |
 * | `tilt`    | `-7 + ((h >>> 7) % 5)`  |
 * | `side`    | `(h & 1) ? 'SIDE B' : 'SIDE A'` |
 *
 * `pair` and `side` both consume the low bit, so a `SIDE B` plate is always an
 * odd-indexed pair. That is intentional in the contract (six pairs × two sides still
 * gives six distinguishable labels) and is not a bug to "fix".
 */
export function derivePlate(performer, title, messageId) {
    const seed = fnv1a(plateKey(performer, title));
    return {
        seed,
        pair: (seed % INK_PAIR_COUNT),
        pattern: ((seed >>> 3) % 3),
        tilt: (-7 + ((seed >>> 7) % 5)),
        side: (seed & 1) === 1 ? 'SIDE B' : 'SIDE A',
        catalogueNo: catalogueNumber(messageId),
    };
}
/**
 * Rebuild a plate from the columns already persisted on a track row, without re-hashing.
 *
 * The indexer stores `plate_seed` and `plate_pair`; the API hands them to the client. A
 * consumer that has the row should call this rather than `derivePlate`, so a plate never
 * silently changes because the row's `title` was cleaned up after indexing. Only `seed`
 * and the message id are needed — every other field is a function of the seed.
 */
export function plateFromSeed(seed, messageId) {
    const h = seed >>> 0;
    return {
        seed: h,
        pair: (h % INK_PAIR_COUNT),
        pattern: ((h >>> 3) % 3),
        tilt: (-7 + ((h >>> 7) % 5)),
        side: (h & 1) === 1 ? 'SIDE B' : 'SIDE A',
        catalogueNo: catalogueNumber(messageId),
    };
}
/**
 * The CSS custom properties a plate needs, ready to spread onto a `style` attribute.
 *
 * Kept here rather than in the component so the mapping from plate → paint lives beside
 * the derivation.
 *
 * ## Why the three inks are NOT in here any more
 *
 * They used to be: this function returned `--plate-bg`, `--plate-ink-1` and
 * `--plate-ink-2` as literal hexes read out of {@link INK_PAIRS}, and the components
 * spread them onto `style={…}`. That made the label the one object in the app a skin
 * could not repaint — an inline custom property beats every stylesheet selector, so
 * `html[data-skin='carbon']` would have produced an achromatic app with a Trojan-orange
 * record in the middle of its main screen.
 *
 * The paint therefore moved to CSS. {@link INK_PAIRS} is unchanged and still the data
 * contract — `tracks.plate_pair` still stores the index, the six entries are still
 * index-stable and still never generated — but the *colours* for a given index are now
 * declared once per skin in `apps/web/src/styles/skins.css`, selected by the
 * `data-pair` attribute the components already emit. {@link inkPairFor} stays the
 * server-side and test-side source of truth for what a pair *is*.
 *
 * What remains here is the one value that is per-track rather than per-skin: the tilt.
 */
export function plateCssVars(plate) {
    return {
        '--plate-tilt': `${plate.tilt}deg`,
    };
}
//# sourceMappingURL=plate.js.map